/**
 *
 * Admin/AssessmentResultTestTool
 *
 */

import React, { useEffect, useState } from 'react';
import _classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { useInjectSaga } from 'utils/injectSaga';
import { useFirebase } from 'react-redux-firebase';
import { useInjectReducer } from 'utils/injectReducer';
import { createStructuredSelector } from 'reselect';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import _isEmpty from 'lodash/isEmpty';
import AppContainer from 'components/AppContainer';
import LoadingOverlay from 'components/LoadingOverlay';
import Header from 'components/Header';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import { makeSelectProfile } from 'containers/Auth/selectors';
import Table from '../AssessmentTool/components/TableComponents/StyledTable';
import TableHeadCell from '../AssessmentTool/components/TableComponents/StyledTableHeadCell';
import TableRow from '../AssessmentTool/components/TableComponents/StyledTableRow';
import TableCell from '../AssessmentTool/components/TableComponents/StyledTableCell';
import {
  makeSelectAssessmentsData,
  makeSelectStoredData,
  makeSelectProcessing,
} from './selectors';
import {
  getAssessmentsData,
  getStoredData,
  submitAssessmentTestData,
} from './actions';
import reducer from './reducer';
import saga from './saga';
import ScoreComponent from './ScoreComponent';

const useStyles = makeStyles(theme => ({
  selectRoot: {
    marginTop: 4,
    '& input': {
      height: '1.2em',
      padding: '16px 14px',
    },
    '& select': {
      height: '1.2em',
      lineHeight: '1.35',
      padding: '16px 14px',
    },
  },
  disabledColor: {
    color: '#999999',
  },
  divider: {
    height: 1,
    width: '100%',
    backgroundColor: '#CECECE',
  },
  noResultsLabel: {
    width: '100%',
    textAlign: 'center',
    padding: 8,
  },
  header: {
    ...theme.typography.subtitle1,
    fontSize: '1.5rem',
  },
  scoreComponentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  fontSize: {
    fontSize: '1.063rem',
  },
  tableBody: {
    '& > tr > th:first-child': {
      width: '99%',
    },
  },
  topAction: {
    marginRight: 16,
    marginLeft: 16,
    '&:first-child': {
      marginLeft: 0,
    },
    '&:last-child': {
      marginRight: 0,
    },
  },
  action: {
    marginTop: 4,
    marginBottom: 4,
    width: '100%',
  },
  label: {
    textAlign: 'center',
  },
  button: {
    ...theme.typography.button,
    color: '#fff',
    boxShadow: 'none',
    width: 139,
    height: 43,
    borderRadius: 2,
    '&:hover': {
      backgroundColor: theme.palette.primary.secondary,
      opacity: 0.8,
    },
  },
}));

const stateSelector = createStructuredSelector({
  data: makeSelectAssessmentsData(),
  processing: makeSelectProcessing(),
  profile: makeSelectProfile(),
  storedData: makeSelectStoredData(),
});

const AssessmentResultTestTool = () => {
  useInjectReducer({ key: 'adminAssessmentResultTest', reducer });
  useInjectSaga({ key: 'adminAssessmentResultTest', saga });

  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const firebase = useFirebase();

  const { storedData, data, processing, profile } = useSelector(stateSelector);

  const [assessment, setAssessment] = useState('-1');
  const [scoreComponents, setScoreComponents] = useState({});
  const [overallScoring, setOverallScoring] = useState('-1');
  const [regularScoring, setRegularScoring] = useState([]);
  const [customAnswers, setCustomAnswers] = useState('');
  const [customAnswersError, setCustomAnswersError] = useState(false);

  useEffect(() => {
    if (
      profile.isLoaded &&
      profile.role !== 'admin' &&
      profile.role !== 'contentAdmin'
    ) {
      history.push('/');
    } else if (profile.isLoaded) {
      dispatch(getStoredData());
      dispatch(getAssessmentsData());
    }
  }, [profile]);

  useEffect(() => {
    setAssessment('-1');
  }, [storedData]);

  useEffect(() => {
    const [item] = data.filter(el => el.slug === assessment);
    if (item) {
      if (item.scoreComponents) {
        setOverallScoring('-1');
        setRegularScoring(
          Array.from(new Array(item.scoreComponents.length)).map(() => '-1'),
        );
      }
      setScoreComponents({
        overallScore: item.overallScore,
        scoreComponents: item.scoreComponents,
      });
    } else {
      setScoreComponents({});
      setOverallScoring('-1');
      setRegularScoring([]);
      setCustomAnswers('');
      setCustomAnswersError(false);
    }
  }, [assessment, data]);

  const handleViewResults = (slug, code) => {
    history.push(`/assessments/${slug}/results?admincode=${code}`);
  };
  const handleDelete = id => {
    const firestore = firebase.firestore();
    firestore
      .collection('assessment_test_data')
      .doc(id)
      .delete()
      .then(() => {
        dispatch(getStoredData());
      });
  };

  const handleAssessmentChange = e => {
    setAssessment(e.target.value);
  };
  const handleOverallScoreChange = e => setOverallScoring(e.target.value);
  const handleScoreComponentChange = index => e => {
    const newScoring = [...regularScoring];
    newScoring.splice(index, 1, e.target.value);
    setRegularScoring(newScoring);
  };
  const handleCustomAnswersChange = e => {
    setCustomAnswers(e.target.value);
    try {
      if (e.target.value !== '') JSON.parse(e.target.value);
      setCustomAnswersError(false);
    } catch (_) {
      setCustomAnswersError(true);
    }
  };
  const handleSubmit = () => {
    dispatch(
      submitAssessmentTestData({
        assessment,
        overallScoring,
        regularScoring,
        customAnswers,
      }),
    );
  };

  return (
    <>
      <Header title="CredibleMind Assessment Results Test Tool" />
      <AppContainer>
        <LoadingOverlay show={processing} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Button
              variant="outlined"
              classes={{ root: classes.topAction }}
              onClick={() => history.push('/admin')}
            >
              Exit
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeadCell>Assessment</TableHeadCell>
                  <TableHeadCell>Code</TableHeadCell>
                  <TableHeadCell>Custom Answers</TableHeadCell>
                  <TableHeadCell>Overall Scoring</TableHeadCell>
                  <TableHeadCell>Regular Scorings</TableHeadCell>
                  <TableHeadCell>Actions</TableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody className={classes.tableBody}>
                {storedData.length > 0 ? (
                  storedData.map(row => (
                    <TableRow key={row.id}>
                      <TableCell>{row.assessment}</TableCell>
                      <TableCell>{row.code}</TableCell>
                      <TableCell>{JSON.stringify(row.customAnswers)}</TableCell>
                      <TableCell>{row.overallScoring}</TableCell>
                      <TableCell>
                        {(row.regularScoring || []).join(', ')}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          classes={{
                            root: classes.action,
                            label: classes.label,
                          }}
                          onClick={() =>
                            handleViewResults(row.assessment, row.code)
                          }
                        >
                          View Results
                        </Button>
                        <Button
                          variant="outlined"
                          classes={{
                            root: classes.action,
                            label: classes.label,
                          }}
                          onClick={() => handleDelete(row.id)}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <td colSpan="5">
                    <Typography className={classes.noResultsLabel}>
                      There are no stored results yet. Create one below.
                    </Typography>
                  </td>
                )}
              </TableBody>
            </Table>
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.divider}></Box>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography className={classes.header} color="primary">
                  Create new test case
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Assessment"
                  onChange={handleAssessmentChange}
                  classes={{
                    root: classes.selectRoot,
                  }}
                  value={assessment}
                  fullWidth
                  select
                  SelectProps={{
                    native: true,
                    classes: {
                      root: _classNames({
                        [classes.disabledColor]: assessment === '-1',
                      }),
                    },
                  }}
                  InputProps={{
                    classes: {
                      root: classes.fontSize,
                    },
                  }}
                  InputLabelProps={{
                    classes: {
                      root: classes.fontSize,
                    },
                  }}
                  variant="outlined"
                >
                  <option value="-1" disabled>
                    Select an assessment
                  </option>
                  {data.map(item => (
                    <option value={item.slug} key={item.slug}>
                      {item.name}
                    </option>
                  ))}
                </TextField>
              </Grid>
              {!_isEmpty(scoreComponents) && (
                <>
                  <Grid item xs={12}>
                    <Typography color="primary" variant="subtitle2">
                      Input the score component values for testing
                    </Typography>
                  </Grid>
                  {scoreComponents.overallScore && (
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        color="textPrimary"
                        gutterBottom
                      >
                        {scoreComponents.overallScore.name}
                      </Typography>
                      <TextField
                        label="Overall scoring"
                        onChange={handleOverallScoreChange}
                        classes={{
                          root: classes.selectRoot,
                        }}
                        value={overallScoring}
                        fullWidth
                        select
                        SelectProps={{
                          native: true,
                          classes: {
                            root: _classNames({
                              [classes.disabledColor]: _isEmpty(
                                scoreComponents.overallScore,
                              ),
                            }),
                          },
                        }}
                        InputProps={{
                          classes: {
                            root: classes.fontSize,
                          },
                        }}
                        InputLabelProps={{
                          classes: {
                            root: classes.fontSize,
                          },
                        }}
                        variant="outlined"
                      >
                        <option value="-1" disabled>
                          Select a value
                        </option>
                        {Array.from(
                          new Array(
                            scoreComponents.overallScore.max -
                              scoreComponents.overallScore.min +
                              1,
                          ),
                        ).map((_, value) => (
                          <option
                            value={value + scoreComponents.overallScore.min}
                            key={value}
                          >
                            {value + scoreComponents.overallScore.min}
                          </option>
                        ))}
                      </TextField>
                    </Grid>
                  )}
                  {!_isEmpty(scoreComponents.scoreComponents) &&
                    scoreComponents.scoreComponents.map((component, i) => (
                      <ScoreComponent
                        key={component.name + i}
                        component={component}
                        onScoreComponentChange={handleScoreComponentChange}
                        index={i}
                        scoreComponents={scoreComponents}
                        regularScoring={regularScoring}
                      />
                    ))}
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      color="textPrimary"
                      gutterBottom
                    >
                      Custom Answers
                    </Typography>
                    <TextField
                      placeholder="Provide Custom Answers in JSON format (Optional)"
                      onChange={handleCustomAnswersChange}
                      value={customAnswers}
                      fullWidth
                      error={customAnswersError}
                      multiline
                      minRows={4}
                      InputProps={{
                        classes: {
                          root: classes.fontSize,
                        },
                      }}
                      InputLabelProps={{
                        classes: {
                          root: classes.fontSize,
                        },
                      }}
                      variant="outlined"
                    />
                  </Grid>
                </>
              )}
              {assessment !== '-1' && (
                <Grid item xs={12}>
                  <Button
                    className={classes.button}
                    onClick={handleSubmit}
                    variant="contained"
                    color="secondary"
                    disabled={
                      (!_isEmpty(scoreComponents.overallScore) &&
                        overallScoring === '-1') ||
                      customAnswersError
                    }
                  >
                    VIEW RESULTS
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </AppContainer>
    </>
  );
};

export default AssessmentResultTestTool;
